import type { EffortScale } from '@heraion/shared';

// Structural t-signature — the repo has no direct i18next dep to import
// TFunction from; every caller passes useTranslation()'s t. Matches the
// convention already used by patternLabel.ts / difficultyLabel.ts.
type Translate = (key: string, opts?: { defaultValue?: string }) => string;

export type EffortWording = {
  shownScale: EffortScale;
  questionText: string;
  effortWord: string;
  shortPrompt: boolean;
};

/** The wording the effort band shows, derived from the set + the athlete's
 *  scale. Pure: no hooks, no state, booleans and t() only. Extracted from
 *  FocusPanelEffort.tsx (SP-FOCUSPANELEFFORT, 2026-07-27). Since #539 an
 *  AMRAP set keeps the ordinary band and wording: the chip is the intention,
 *  the band is the outcome, and the target ring marks RIR 0. */
export function deriveEffortWording({
  isDuration,
  effortScale,
  t,
}: {
  isDuration: boolean;
  effortScale: EffortScale;
  t: Translate;
}): EffortWording {
  // The athlete-facing prompt: RIR asks what's left in the tank, RPE asks how
  // hard it felt (same effort read from opposite ends). One string for both the
  // visible question and its a11y label.
  // A duration set reads the band as RPE whatever the athlete's own scale (D1):
  // on a hold there are no reps left in the tank to count, so the RIR question
  // has no answer. The canonical value written is unaffected: this is print.
  const shownScale: EffortScale = isDuration ? 'rpe' : effortScale;
  const questionText =
    shownScale === 'rir'
      ? t('mobile.logger.effortQuestionRir', {
          defaultValue: 'Reps in reserve?',
        })
      : t('mobile.logger.effortQuestionRpe', {
          defaultValue: 'How hard was it?',
        });
  // The compact variant says the one word instead of the whole question: it
  // has 76pt on the axis, not 244 (D11). The full question stays the spoken
  // label, so a screen reader still hears what's being asked.
  const effortWord = t('mobile.logger.effortWord', { defaultValue: 'Effort' });
  const shortPrompt = isDuration;

  return { shownScale, questionText, effortWord, shortPrompt };
}
